// Wine Camp participating wineries data
// Each winery gets a landing page at /winecamp/:slug

export interface Winery {
  slug: string;
  name: string;
  logo?: string;
  winemakerPhoto?: string;
  bottlePhoto?: string;
  winemakerNames: string;
  tasteWithNames?: string;
  instagram?: string;
  website?: string;
  blurb: string;
  pronoun?: 'him' | 'her' | 'them';
}

// Import winemaker photos
import rymeCellarsPortrait from "@/assets/may/wineries/ryme-cellars-portrait.jpg";
import rymeBottle from "@/assets/may/wineries/ryme-bottle.jpg";
import bloodrootPortrait from "@/assets/may/wineries/bloodroot-portrait.webp";
import bloodrootBottle from "@/assets/may/wineries/bloodroot-bottle.jpg";
import extradimensionalPortrait from "@/assets/may/wineries/extradimensional-portrait.jpg";
import extradimensionalBottle from "@/assets/may/wineries/extradimensional-bottle.jpg";
import liocoPortrait from "@/assets/may/wineries/lioco-portrait.jpg";
import liocoBottle from "@/assets/may/wineries/lioco-bottle.jpg";
import rootdownPortrait from "@/assets/may/wineries/rootdown-portrait.jpg";
import rootdownBottle from "@/assets/may/wineries/rootdown-bottle.jpg";
import keepWinesPortrait from "@/assets/may/wineries/keep-wines-portrait.jpg";
import keepWinesBottle from "@/assets/may/wineries/keep-wines-bottle.jpg";
import trailmarkerPortrait from "@/assets/may/wineries/trailmarker-portrait.jpg";
import trailmarkerBottle from "@/assets/may/wineries/trailmarker-bottle.jpg";
import arnotRobertsPortrait from "@/assets/may/wineries/arnot-roberts-portrait.jpg";
import arnotRobertsBottle from "@/assets/may/wineries/arnot-roberts-bottle.webp";
import mariettaCellarsPortrait from "@/assets/may/wineries/marietta-cellars-portrait.jpg";
import mariettaCellarsBottle from "@/assets/may/wineries/marietta-cellars-bottle.jpg";
import actaPortrait from "@/assets/may/wineries/acta-portrait.jpg";
import actaBottle from "@/assets/may/wineries/acta-bottle.jpg";
import belongPortrait from "@/assets/may/wineries/belong-portrait.jpg";
import belongBottle from "@/assets/may/wineries/belong-bottle.jpg";
import dujuPortrait from "@/assets/may/wineries/duju-portrait.jpg";
import dujuBottle from "@/assets/may/wineries/duju-bottle.png";

// Import logos
import rymeLogo from "@/assets/may/wineries/logos/ryme-logo.png";
import bloodrootLogo from "@/assets/may/wineries/logos/bloodroot-logo.png";
import extradimensionalLogo from "@/assets/may/wineries/logos/extradimensional-logo.png";
import liocoLogo from "@/assets/may/wineries/logos/lioco-logo.png";
import rootdownLogo from "@/assets/may/wineries/logos/rootdown-logo.png";
import keepWinesLogo from "@/assets/may/wineries/logos/keep-wines-logo.png";
import trailmarkerLogo from "@/assets/may/wineries/logos/trailmarker-logo.png";
import arnotRobertsLogo from "@/assets/may/wineries/logos/arnot-roberts-logo.png";
import mariettaLogo from "@/assets/may/wineries/logos/marietta-logo.png";
import actaLogo from "@/assets/may/wineries/logos/acta-logo.png";
import belongLogo from "@/assets/may/wineries/logos/belong-logo.png";
import dujuLogo from "@/assets/may/wineries/logos/duju-logo.png";

export const wineries: Winery[] = [
  {
    slug: "ryme",
    name: "Ryme Cellars",
    logo: rymeLogo,
    winemakerPhoto: rymeCellarsPortrait,
    bottlePhoto: rymeBottle,
    winemakerNames: "Ryan & Megan Glaab",
    instagram: "ryme_cellars",
    website: "https://www.rymecellars.com",
    blurb: "A husband-and-wife project with old world instincts and California roots. Ryan and Megan have been growing their portfolio since 2007, starting with a single ton of Aglianico. They specialize in Italian varietals — Vermentino, Aglianico, Ribolla Gialla, Fiano — alongside California classics. No cultured yeast, no fining, no filtering. The wines are encouraged, never controlled.",
  },
  {
    slug: "bloodroot",
    name: "Bloodroot Wines",
    logo: bloodrootLogo,
    winemakerPhoto: bloodrootPortrait,
    bottlePhoto: bloodrootBottle,
    winemakerNames: "Noah & Kelly Dorrance",
    instagram: "bloodrootwines",
    website: "https://www.bloodrootwines.com",
    blurb: "A Sonoma-based winery rooted in place and built around community by Noah and Kelly Dorrance. They work with cool-climate vineyards across Petaluma Gap, Anderson Valley, and the Sonoma Coast, making wines that are bright, layered, and meant to be shared. Pinot Noir, Chenin Blanc, Carignan, Tocai Friulano — always small-production, always hands-on. Their downtown Healdsburg tasting room doubles as a gathering spot, and their Ramble events blur the line between wine club and house party.",
  },
  {
    slug: "extradimensional",
    name: "Extradimensional Wine Co. Yeah!",
    logo: extradimensionalLogo,
    winemakerPhoto: extradimensionalPortrait,
    bottlePhoto: extradimensionalBottle,
    winemakerNames: "Hardy Wallace",
    tasteWithNames: "Jimmy & Ale",
    instagram: "winecoyeah",
    website: "https://www.winecoyeah.com",
    blurb: "A one-man, all-vibes operation by Hardy Wallace making wine that's as offbeat and joyful as the name suggests. Based in Sonoma County, Hardy works with grapes from across California and beyond, turning Carignan, Cinsault, Verdelho, and whatever else catches his eye into wines that are wild, textured, and completely unpredictable. No formula, no house style — just curiosity, good instincts, and a deep love for the weird and wonderful corners of winemaking.",
  },
  {
    slug: "lioco",
    name: "LIOCO Wine",
    logo: liocoLogo,
    winemakerPhoto: liocoPortrait,
    bottlePhoto: liocoBottle,
    winemakerNames: "Matt & Sara Licklider",
    tasteWithNames: "Matt Licklider & Maegan Lavelle",
    instagram: "liocowineco",
    website: "https://www.liocowine.com",
    blurb: "Matt and Sara Licklider's quiet rebellion against overworked California wine. Founded in 2005, the project is built on a simple idea: let the vineyard do the talking. With winemaker Drew Huffine in the cellar, they source from cool-climate sites across Sonoma County and beyond — Chardonnay, Pinot Noir, Carignan — and keep their hands off as much as possible. No new oak, no heavy extraction, no chasing scores. The wines are lean, bright, and a little contrarian. Their Healdsburg tasting room feels more like a friend's living room than a winery.",
  },
  {
    slug: "rootdown",
    name: "Rootdown Wine Cellars",
    logo: rootdownLogo,
    winemakerPhoto: rootdownPortrait,
    bottlePhoto: rootdownBottle,
    winemakerNames: "Mike Lucia",
    instagram: "rootdownwines",
    website: "https://rootdownwine.com",
    blurb: "Mike Lucia is a winemaker rooted in Sonoma County's Dry Creek Valley who farms his own vineyards and makes wine with an eye toward the land. His focus is on old-vine Zinfandel, Carignane, and field blends from heritage sites, alongside Chenin Blanc and other varieties that speak to place. The approach is minimal — native ferments, neutral oak, no fining or filtering — and the results are wines that feel alive, grounded, and distinctly Californian.",
  },
  {
    slug: "keep",
    name: "Keep Wines",
    logo: keepWinesLogo,
    winemakerPhoto: keepWinesPortrait,
    bottlePhoto: keepWinesBottle,
    winemakerNames: "Johanna Jensen & Jack Roberts",
    instagram: "keepwines",
    website: "https://www.thekeepwines.com",
    blurb: "Johanna Jensen and Jack Roberts built this Napa-based project on curiosity and a love for California's overlooked corners. They source from old-vine sites across the state — Clarksburg, Lodi, Mendocino, the Delta — and work with varieties like Chenin Blanc, Colombard, Counoise, and Ciliegiolo that most California wineries ignore. The approach is hands-off: native ferments, neutral vessels, no fining or filtering. The wines are bright, textured, and generous — built for the table, not the tasting note.",
  },
  {
    slug: "trailmarker",
    name: "Trail Marker Wine Co.",
    logo: trailmarkerLogo,
    winemakerPhoto: trailmarkerPortrait,
    bottlePhoto: trailmarkerBottle,
    winemakerNames: "Drew Huffine & Emily Virgil",
    instagram: "trailmarkerwineco",
    blurb: "Drew Huffine and Emily Virgil run this Santa Rosa-based project rooted in curiosity and a deep respect for California's lesser-known vineyard sites. They source from family-owned, responsibly farmed plots across the Santa Cruz Mountains, Mendocino Coast, and Lodi, working with varieties like Kerner, Ciliegiolo, Counoise, and Carignan that don't show up on most California wine lists. The winemaking is minimal-intervention — high acid, low alcohol, well-balanced expressions of place. Their sister label, Kid Sister, takes the same ethos in a playful, no-rules direction.",
  },
  {
    slug: "arnot-roberts",
    name: "Arnot-Roberts",
    logo: arnotRobertsLogo,
    winemakerPhoto: arnotRobertsPortrait,
    bottlePhoto: arnotRobertsBottle,
    winemakerNames: "Duncan Arnot Meyers & Nathan Lee Roberts",
    tasteWithNames: "Duncan & Nathan",
    instagram: "arnotrobertswinery",
    website: "https://www.arnotroberts.com",
    blurb: "Childhood friends Duncan Arnot Meyers and Nathan Lee Roberts started making wine together in 2001 and never stopped. Based in Healdsburg, they work with a network of exceptional vineyards across Northern California, from the slopes of the Sierra Foothills to the fog-swept ridges of the Sonoma Coast. The focus is on site-specific wines — Trousseau, Ribolla Gialla, Syrah, Cabernet Franc — made with minimal intervention and maximum attention. Duncan handles the farming and winemaking; Nathan, a master cooper, builds the barrels. The wines are precise, restrained, and quietly profound.",
  },
  {
    slug: "marietta",
    name: "Marietta Cellars",
    logo: mariettaLogo,
    winemakerPhoto: mariettaCellarsPortrait,
    bottlePhoto: mariettaCellarsBottle,
    winemakerNames: "Scot Bilbro",
    instagram: "mariettacellars",
    website: "https://www.mariettacellars.com",
    blurb: "Second-generation winemaker Scot Bilbro carries forward the legacy his father Chris started in 1978 in Geyserville. The winery is built on old-vine Sonoma County fruit and a no-nonsense approach: field blends, Zinfandel, Cabernet Sauvignon, and Petite Sirah made to drink well without pretense. The flagship Old Vine Red has been a California staple for decades — an affordable, crowd-pleasing blend that never tries too hard. Scot keeps the focus on value, consistency, and the kind of wines that belong on every table.",
  },
  {
    slug: "acta",
    name: "ACTA Wine",
    logo: actaLogo,
    winemakerPhoto: actaPortrait,
    bottlePhoto: actaBottle,
    winemakerNames: "Dustin Moilanen",
    tasteWithNames: "Dustin & Mark",
    instagram: "actawine",
    website: "https://actawine.com",
    blurb: "Dustin Moilanen built this estate winery in Alexander Valley on the Latin creed Acta Non Verba: Deeds, Not Words. Every decision is intentional, from farming their own vineyards to the winemaking process. The focus is on bold, estate-grown Sonoma County wines — Cabernet Sauvignon, Zinfandel, Petite Sirah — made with purpose and without shortcuts. The tasting lounge and patio sit right on the property, and the wines reflect the land they come from: structured, confident, and built to last.",
  },
  {
    slug: "belong",
    name: "Belong Wine Co.",
    logo: belongLogo,
    winemakerPhoto: belongPortrait,
    bottlePhoto: belongBottle,
    winemakerNames: "Bertus & Alli van Zyl",
    instagram: "belongwineco",
    website: "https://belongwines.com",
    blurb: "A husband-and-wife project by Bertus and Alli van Zyl, born from a single ton of fruit and a shared love of wild places. Bertus grew up in Cape Town, Alli in the States, and together they landed in Napa with a focus on the Sierra Foothills. The wines are built around high-elevation El Dorado County fruit — Mourvèdre, Syrah, Grenache — made with native ferments, a light touch, and zero pretense. The labels are as adventurous as the wines: free-spirited, a little sun-faded, and meant for drinking outside.",
  },
  {
    slug: "duju",
    name: "Duju",
    logo: dujuLogo,
    winemakerPhoto: dujuPortrait,
    bottlePhoto: dujuBottle,
    winemakerNames: "Erika Dawkins",
    instagram: "sipduju",
    website: "https://sipduju.com",
    blurb: "A luxury non-alcoholic sparkling wine produced by Erika Dawkins, born from a deep appreciation for the craft of great wine, not a rejection of it. Sourced from France and built to the highest standards of purity and taste, Duju fills a clear gap: a truly sophisticated, high-quality non-alcoholic sparkling for those who love the ritual, the toast, and the elegance of bubbles but prefer to stay sharp. It's an invitation to choose a different sip — one as crisp and intentional as the life you're building.",
    pronoun: "her",
  },
];

export const getWineryBySlug = (slug: string): Winery | undefined => {
  return wineries.find(w => w.slug === slug);
};
